import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Button,
    Table,
    Label,
} from "reactstrap";
import {
    getTaxInvoiceReport,
    exportTaxInvoiceReportToExcel
} from "../../components/Api/GmsBilling";
import { getBranches } from "../../components/Api/GmsBranch";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import Select from "react-select";
import { Filter, Download } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import {
    hasPagePermissionWithAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { useNavigate } from "react-router-dom";

export default function TaxInvoiceReportPage() {
    const navigate = useNavigate();
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [showFilters, setShowFilters] = useState(true);

    // Default to current month
    const [selectedMonth, setSelectedMonth] = useState(new Date());

    // Company and Branch filter states
    const [businesses, setBusinesses] = useState([]);
    const [allBranches, setAllBranches] = useState([]); // Master list of all branches
    const [branchOptions, setBranchOptions] = useState([]); // Filtered options for dropdown
    const [selectedBusiness, setSelectedBusiness] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [businessLoading, setBusinessLoading] = useState(false);
    const [branchLoading, setBranchLoading] = useState(false);

    // Get user's context from localStorage
    const userCompanyId = localStorage.getItem("userCompanyId") ? Number(localStorage.getItem("userCompanyId")) : null;
    const userBranchId = localStorage.getItem("userBranchId") ? Number(localStorage.getItem("userBranchId")) : null;

    // Check permissions
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
    const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsTaxInvoiceReport');

    const currencyCode = localStorage.getItem("currencyCode") || "AED";

    // Helper to format currency
    const formatCurrency = (amount) => {
        return `${currencyCode} ${new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0)}`;
    };

    // Helper to format date for API (YYYY-MM-DD)
    const formatDateForAPI = (date) => {
        if (!date) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Helper to format date for Display (DD/MM/YYYY)
    const formatDateForDisplay = (dateString) => {
        try {
            const date = dateString instanceof Date ? dateString : new Date(dateString);
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        } catch (error) {
            return "N/A";
        }
    };

    const loadBusinesses = useCallback(async () => {
        try {
            setBusinessLoading(true);
            const data = await getBusinesses({ business_Active: true });
            const options = (data || []).map(biz => ({
                value: biz.business_Id,
                label: biz.business_NameEn
            }));
            setBusinesses(options);

            // Set default business if available
            if (userCompanyId) {
                const defaultBiz = options.find(o => o.value === userCompanyId);
                if (defaultBiz) setSelectedBusiness(defaultBiz);
            }
        } catch (error) {
            console.error("Error loading businesses:", error);
        } finally {
            setBusinessLoading(false);
        }
    }, [userCompanyId]);

    const loadAllBranches = useCallback(async () => {
        try {
            setBranchLoading(true);
            const data = await getBranches({ branch_Active: true });
            setAllBranches(data || []);
        } catch (error) {
            console.error("Error loading branches:", error);
        } finally {
            setBranchLoading(false);
        }
    }, []);

    useEffect(() => {
        if (hasPermission) {
            loadBusinesses();
            loadAllBranches();
        }
    }, [hasPermission, loadBusinesses, loadAllBranches]);

    // Handle company change and filter branches
    useEffect(() => {
        if (selectedBusiness && allBranches.length > 0) {
            // Filter branches based on selected business
            // Use branch_Buisness_Id (note the typo in backend model)
            const filtered = allBranches
                .filter(b => b.branch_Buisness_Id === selectedBusiness.value || b.branch_BusinessId === selectedBusiness.value)
                .map(branch => ({
                    value: branch.branch_Id,
                    label: branch.branch_NameEn
                }));

            setBranchOptions(filtered);

            // Set default branch if it belongs to the selected business and matches userBranchId
            if (userBranchId) {
                const defaultBranch = filtered.find(o => o.value === userBranchId);
                if (defaultBranch) {
                    setSelectedBranch(defaultBranch);
                } else {
                    setSelectedBranch(null);
                }
            } else {
                setSelectedBranch(null);
            }
        } else {
            setBranchOptions([]);
            setSelectedBranch(null);
        }
    }, [selectedBusiness, allBranches, userBranchId]);

    const loadReportData = useCallback(async () => {
        if (!selectedMonth) return;

        try {
            setLoading(true);

            // Calculate start and end of the selected month
            const fromDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
            const toDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0);

            const filterData = {
                fromDate: formatDateForAPI(fromDate),
                toDate: formatDateForAPI(toDate),
                business_Id: selectedBusiness?.value || null,
                branch_Id: selectedBranch?.value || null,
            };

            console.log("📊 Loading tax invoice report with filter:", filterData);

            const response = await getTaxInvoiceReport(filterData);

            console.log("📊 Report response:", response);

            // Handle response structure (adjust based on actual API return)
            const data = response?.data || response || [];

            if (Array.isArray(data)) {
                setReportData(data);
            } else {
                setReportData([]);
            }
        } catch (error) {
            if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                Swal.fire({
                    title: "Session Timed Out",
                    text: "Your session has expired. Please login again.",
                    icon: "warning",
                    confirmButtonText: "Login",
                    allowOutsideClick: false
                }).then(() => {
                    navigate("/login");
                });
                return;
            }
            console.error("❌ Error loading report data:", error);
            Swal.fire("Error", "Failed to load tax invoice report", "error");
            setReportData([]);
        } finally {
            setLoading(false);
        }
    }, [selectedMonth, selectedBusiness, selectedBranch, navigate]);

    useEffect(() => {
        if (hasPermission) {
            loadReportData();
        } else {
            window.location.href = '/Dashboard';
        }
    }, [hasPermission, loadReportData]);

    const handleExport = async () => {
        try {
            setExporting(true);

            const fromDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
            const toDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0);

            const filterData = {
                fromDate: formatDateForAPI(fromDate),
                toDate: formatDateForAPI(toDate),
                business_Id: selectedBusiness?.value || null,
                branch_Id: selectedBranch?.value || null,
            };

            await exportTaxInvoiceReportToExcel(filterData);
            Swal.fire("Success", "Tax invoice report exported successfully", "success");
        } catch (error) {
            if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                Swal.fire({
                    title: "Session Timed Out",
                    text: "Your session has expired. Please login again.",
                    icon: "warning",
                    confirmButtonText: "Login",
                    allowOutsideClick: false
                }).then(() => {
                    navigate("/login");
                });
                return;
            }
            console.error("❌ Error exporting report:", error);
            Swal.fire("Error", "Failed to export tax invoice report", "error");
        } finally {
            setExporting(false);
        }
    };

    // Calculate totals
    const totals = useMemo(() => {
        return reportData.reduce((acc, row) => ({
            amount: acc.amount + (row.Amount || row.amount || 0),
            discount: acc.discount + (row.DiscountAmount || row.discountAmount || 0),
            taxable: acc.taxable + (row.TaxableAmount || row.taxableAmount || 0),
            tax: acc.tax + (row.TaxAmount || row.taxAmount || 0),
            net: acc.net + (row.NetAmount || row.netAmount || 0),
        }), {
            amount: 0,
            discount: 0,
            taxable: 0,
            tax: 0,
            net: 0,
        });
    }, [reportData]);

    if (!hasPermission) {
        return (
            <div className="page-content">
                <Container fluid>
                    <div className="text-center">
                        <h4>Access Denied</h4>
                        <p>You don't have permission to access this page.</p>
                    </div>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumb title="Tax Invoice Report" breadcrumbItem="Reports" />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h4 className="card-title mb-0">
                                        Tax Invoice Report
                                    </h4>
                                    <div>
                                        <Button
                                            color="primary"
                                            className="me-2"
                                            onClick={() => setShowFilters(!showFilters)}
                                        >
                                            <Filter className="me-1" size={16} />
                                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                                        </Button>
                                        <Button
                                            color="success"
                                            onClick={handleExport}
                                            disabled={loading || exporting || reportData.length === 0}
                                        >
                                            {exporting ? <ClipLoader size={14} color="#fff" /> : <Download className="me-1" size={16} />}
                                            Export to Excel
                                        </Button>
                                    </div>
                                </div>

                                {/* Filter Section */}
                                {showFilters && (
                                    <Card className="mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                                        <CardBody>
                                            <Row className="align-items-end">
                                                <Col md={3}>
                                                    <div className="mb-3">
                                                        <Label>Select Month</Label>
                                                        <DatePicker
                                                            selected={selectedMonth}
                                                            onChange={(date) => setSelectedMonth(date)}
                                                            dateFormat="MM/yyyy"
                                                            showMonthYearPicker
                                                            className="form-control"
                                                            maxDate={new Date()} // No future months
                                                            placeholderText="Select Month"
                                                        />
                                                    </div>
                                                </Col>
                                                <Col md={3}>
                                                    <div className="mb-3">
                                                        <Label>Company</Label>
                                                        <Select
                                                            value={selectedBusiness}
                                                            onChange={(option) => setSelectedBusiness(option)}
                                                            options={businesses}
                                                            isClearable
                                                            placeholder="All Companies"
                                                            isLoading={businessLoading}
                                                        />
                                                    </div>
                                                </Col>
                                                <Col md={3}>
                                                    <div className="mb-3">
                                                        <Label>Branch</Label>
                                                        <Select
                                                            value={selectedBranch}
                                                            onChange={(option) => setSelectedBranch(option)}
                                                            options={branchOptions}
                                                            isClearable
                                                            placeholder="All Branches"
                                                            isLoading={branchLoading}
                                                            isDisabled={!selectedBusiness}
                                                        />
                                                    </div>
                                                </Col>
                                                {/* Add more filters if needed */}
                                            </Row>
                                        </CardBody>
                                    </Card>
                                )}

                                {/* Table Section */}
                                <div className="table-responsive">
                                    {loading ? (
                                        <div className="text-center py-4">
                                            <ClipLoader color="#556ee6" loading={loading} size={35} />
                                            <p className="mt-2">Loading report...</p>
                                        </div>
                                    ) : (
                                        <Table bordered hover striped responsive>
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Sl No</th>
                                                    <th>Invoice Date</th>
                                                    <th>Invoice No</th>
                                                    <th>Customer Name</th>
                                                    <th className="text-end">Amount</th>
                                                    <th className="text-end">Discount Amount</th>
                                                    <th className="text-end">Taxable Amount</th>
                                                    <th className="text-end">Tax Amount</th>
                                                    <th className="text-end">Net Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {reportData.length > 0 ? (
                                                    reportData.map((row, index) => (
                                                        <tr key={index}>
                                                            <td>{row.SlNo || row.slNo || index + 1}</td>
                                                            <td>{formatDateForDisplay(row.InvoiceDate || row.invoiceDate)}</td>
                                                            <td>{row.InvoiceNumber || row.invoiceNumber}</td>
                                                            <td>{row.CustomerName || row.customerName}</td>
                                                            <td className="text-end">{formatCurrency(row.Amount || row.amount)}</td>
                                                            <td className="text-end">{formatCurrency(row.DiscountAmount || row.discountAmount)}</td>
                                                            <td className="text-end">{formatCurrency(row.TaxableAmount || row.taxableAmount)}</td>
                                                            <td className="text-end">{formatCurrency(row.TaxAmount || row.taxAmount)}</td>
                                                            <td className="text-end"><strong>{formatCurrency(row.NetAmount || row.netAmount)}</strong></td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="9" className="text-center">No records found for the selected month</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                            {reportData.length > 0 && (
                                                <tfoot>
                                                    <tr className="table-light font-weight-bold">
                                                        <td colSpan="4" className="text-end"><strong>Total</strong></td>
                                                        <td className="text-end"><strong>{formatCurrency(totals.amount)}</strong></td>
                                                        <td className="text-end"><strong>{formatCurrency(totals.discount)}</strong></td>
                                                        <td className="text-end"><strong>{formatCurrency(totals.taxable)}</strong></td>
                                                        <td className="text-end"><strong>{formatCurrency(totals.tax)}</strong></td>
                                                        <td className="text-end"><strong>{formatCurrency(totals.net)}</strong></td>
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </Table>
                                    )}
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}
